"use client";

import React, { useState, useEffect } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from "@/components/ui/carousel";
import { Sparkles, Star, Wand } from "lucide-react";
import { motion } from "framer-motion";
interface MagicMoment {
  title: string;
  description: string;
  image: string;
  alt: string;
}
const MOMENTS: MagicMoment[] = [{
  title: "Unforgettable Memories",
  description: "Long after the event ends, your guests will remember how it felt — joyful, creative, special, and purely magical.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562315/img/unforgettable_memories_sparkling_celebration.png",
  alt: "A magical event moment captured with sparkling lights and joyful guests"
}, {
  title: "Guest of Honor Spotlights",
  description: "Every event includes a thoughtfully designed moment where the guest of honor is truly seen, celebrated, and elevated.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562315/img/guest_of_honor_spotlight_moment.png",
  alt: "A joyful guest of honor being celebrated under a warm spotlight with magical sparkles"
}, {
  title: "Stress-Free Hosting",
  description: "You enjoy the celebration. We handle the details, flow, setup, and cleanup — so you never feel overwhelmed.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562315/img/woman_giving_toast_magical_celebration.png",
  alt: "A woman giving a toast with a sparkling glass at a magical event"
}, {
  title: "Your Vision, Magically Brought to Life",
  description: "We listen carefully and design intentionally — transforming your ideas into a cohesive, memorable experience.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562315/img/creative_enchanted_party_atmosphere.png",
  alt: "Wildflower and rose centerpiece with gold accents"
}, {
  title: "Magical Creative Experiences",
  description: "Every event is built around an engaging, hands-on experience that sparks creativity, connection, and joy.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562315/img/magical_paint_and_sip_experience.png",
  alt: "A vibrant adult paint and sip party with participants creating art together in a magical atmosphere"
}];
const GlitterBackground = () => {
  const [particles] = useState(() => Array.from({
    length: 50
  }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    delay: Math.random() * 5,
    duration: 3 + Math.random() * 4,
    size: 2 + Math.random() * 4
  })));
  return <div className="absolute inset-0 z-0 pointer-events-none opacity-60 overflow-hidden">
      {particles.map(particle => <motion.div key={particle.id} className="absolute rounded-full bg-secondary" style={{
      left: `${particle.x}%`,
      width: `${particle.size}px`,
      height: `${particle.size}px`,
      boxShadow: "0 0 8px rgba(212, 175, 55, 0.8)"
    }} initial={{
      y: "100vh",
      opacity: 0
    }} animate={{
      y: "-20vh",
      opacity: [0, 1, 1, 0],
      x: [0, Math.sin(particle.id) * 30, 0]
    }} transition={{
      duration: particle.duration,
      delay: particle.delay,
      repeat: Infinity,
      ease: "linear"
    }} />)}
    </div>;
};
export default function SignatureMagicMoments() {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!api) return;
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);
  return <section id="signature-magic-moments" className="relative w-full pt-44 pb-24 overflow-hidden text-white">
      <GlitterBackground />

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div className="mt-16 mb-20 text-center max-w-3xl mx-auto">
          <div className="flex items-center justify-center gap-2 mb-4 text-secondary">
            <Sparkles className="w-5 h-5" />
            <span className="uppercase tracking-widest font-semibold !text-2xl">THE EXPERIENCE</span>
            <Sparkles className="w-5 h-5" />
          </div>
          <h2 className="text-4xl md:text-6xl text-primary mb-6 tracking-tight">
            Signature Magic Moments
          </h2>
          <p className="text-xl leading-relaxed text-white/90 !italic md:text-2xl">Immerse yourself in a world where every detail is curated to enchant. <br />We craft magical experiences that linger in memory.</p>
        </div>

        <div className="relative">
          <Carousel setApi={setApi} opts={{
          align: "center",
          loop: true
        }} className="w-full">
            <CarouselContent className="-ml-4 md:-ml-8">
              {MOMENTS.map((moment, index) => <CarouselItem key={index} className="pl-4 md:pl-8 md:basis-2/3 lg:basis-1/2 xl:basis-2/5">
                  <div className="group relative h-[500px] w-full overflow-hidden rounded-[24px] shadow-2xl transition-transform duration-500 hover:scale-[1.02]" data-index={index}>
                    <div className="absolute inset-0 bg-primary/10">
                      <img src={moment.image} alt={moment.alt} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    </div>

                    <div className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-primary/95 via-primary/80 to-transparent pt-24 translate-y-4 transition-transform duration-500 group-hover:translate-y-0">
                      {}
                      <div className="relative backdrop-blur-xl bg-white/20 border border-secondary p-6 rounded-2xl shadow-lg">
                        <div className="absolute -top-6 right-6 bg-secondary text-secondary-foreground p-3 rounded-full shadow-lg">
                          <Wand className="w-6 h-6" />
                        </div>
                        <h3 className="text-2xl font-bold mb-3 drop-shadow-md text-white md:text-4xl">
                          {moment.title}
                        </h3>
                        {}
                        <p className="text-white/90 leading-relaxed text-sm md:text-base">
                          {moment.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </CarouselItem>)}
            </CarouselContent>

            <div className="hidden md:block">
              <CarouselPrevious className="left-8 bg-background/80 hover:bg-secondary hover:text-secondary-foreground border-none shadow-lg backdrop-blur-sm h-12 w-12 [&_svg]:size-6" />
              <CarouselNext className="right-8 bg-background/80 hover:bg-secondary hover:text-secondary-foreground border-none shadow-lg backdrop-blur-sm h-12 w-12 [&_svg]:size-6" />
            </div>
          </Carousel>

          <div className="flex justify-center gap-2 mt-8 md:hidden">
            {Array.from({
            length: count
          }).map((_, i) => <Button key={i} variant="ghost" size="sm" className={`p-0 h-2 rounded-full transition-all duration-300 hover:bg-transparent ${i === current - 1 ? "w-8 bg-secondary" : "w-2 bg-primary/20"}`} onClick={() => api?.scrollTo(i)} aria-label={`Go to slide ${i + 1}`} />)}
          </div>
        </div>

        <div className="mt-16 text-center">
          <Button asChild size="lg" variant="secondary" className="rounded-full px-8 py-6 text-lg shadow-xl shadow-secondary/20 relative overflow-hidden group/btn">
            <Link to="#summon-the-magic">
              <motion.div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent pointer-events-none" initial={{
              x: "-100%"
            }} animate={{
              x: "200%"
            }} transition={{
              repeat: Infinity,
              duration: 3,
              ease: "easeInOut",
              repeatDelay: 1
            }} style={{
              skewX: -20
            }} />
              <Star className="mr-2 h-5 w-5 fill-current relative z-10" />
              <span className="relative z-10 md:text-xl">Start Your Magical Journey</span>
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}